import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    useDeleteUserSessionApi,
    UserSessionDTO,
    useSaveUserSessionApi,
    useSearchUserSessionApi,
} from '../../../api/user-session-api';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { DateCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveUserSessionModal } from './save-user-session-modal';

export function UserSessionApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedUserSession, setSelectedUserSession] = useState<UserSessionDTO | undefined>();
    const userSessionInit = useSearchUserSessionApi();
    const saveUserSession = useSaveUserSessionApi();
    const deleteUserSession = useDeleteUserSessionApi();

    const { result, error, busy, initialized, initializing } = userSessionInit;

    const userAgentTitle = useCallback(
        (id?: number) => {
            var userAgent = result?.userAgents.find(x => x.id === id);
            return <span>{userAgent ? `${userAgent.os} ` : id}</span>;
        },
        [result?.userAgents]
    );

    const columns: IBasicTableColumn<UserSessionDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 40,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>user-name</T>,
                name: 'userName',
            },
            {
                Header: <T>refresh-count</T>,
                name: 'refreshCount',
                width: 80,
                Cell: ({ row }) => <N>{row.refreshCount}</N>,
            },
            {
                Header: <T>refresh-token-date</T>,
                name: 'refreshTokenDate',
                width: 40,
                Cell: DateCell,
            },
            {
                Header: <T>last-ip</T>,
                name: 'lastIP',
                width: 80,
            },
            {
                Header: <T>user-agent-id</T>,
                name: 'userAgentId',
                width: 80,
                Cell: ({ row }) => userAgentTitle(row.userAgentId),
            },
        ],
        [userAgentTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        userSessionInit.post(null, {});
    }, [initialized, initializing, error, userSessionInit]);

    const handleDelete = (userSession: UserSessionDTO) => {
        bd.msgbox(
            translate('delete-user-session'),
            translate('confirm-delete-user-session'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteUserSession.remove(
                            userSession,
                            () => {
                                userSessionInit.updateResult({
                                    ...result,
                                    userSessions: result.userSessions.filter(
                                        b => b.id !== userSession.id
                                    ),
                                });
                                notify.dark(
                                    translate('selected-user-session-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedUserSession(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (userSession: UserSessionDTO) => {
        saveUserSession.post(
            null,
            userSession,
            res => {
                if (selectedUserSession) {
                    userSessionInit.updateResult({
                        ...result,
                        userSessions: result.userSessions.map(b =>
                            b.id === userSession.id ? res.userSession : b
                        ),
                    });
                } else {
                    userSessionInit.updateResult({
                        ...result,
                        userSessions: [res.userSession, ...result.userSessions],
                    });
                }

                setShowModal(false);
                setSelectedUserSession(undefined);
                notify.success(
                    selectedUserSession
                        ? translate('selected-user-session-updated-successfully')
                        : translate('new-user-session-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => userSessionInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar onGoBack="/home" title="user-session" />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.userSessions}
                        onRowClick={({ row }) => {
                            setSelectedUserSession(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveUserSessionModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedUserSession(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedUserSession={selectedUserSession}
                        userAgents={result?.userAgents}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
